// mindmetal-app/web/src/pages/employee/mindmetal/index.tsx
import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Brain,
  Settings,
  Users,
  Beaker,
  User,
  Activity,
  Monitor,
  BarChart3,
  Eye,
  Download,
  ExternalLink,
  Shield,
  Sliders,
  Scale,
  GitCompareArrows,
} from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@personic/shared'
import { ConfigTab } from './ConfigTab'
import { TestUsersTab } from './TestUsersTab'
import { DistillationTesterTab } from './DistillationTesterTab'
import { PersonaTesterTab } from './PersonaTesterTab'
import { SignalsTab } from './SignalsTab'
import { RenderingTab } from './RenderingTab'
import { MetricsTab } from './MetricsTab'
import { HumanReviewTab } from './HumanReviewTab'
import { ExportTab } from './ExportTab'
import { ComparisonTab } from './ComparisonTab'

interface TabDef {
  value: string
  label: string
  icon: React.ElementType
  external?: string // If set, renders as an external link instead of inline tab
}

const TABS: TabDef[] = [
  { value: 'test-users', label: 'Test Users', icon: Users },
  { value: 'config', label: 'Config', icon: Settings },
  { value: 'distillation', label: 'Distillation', icon: Beaker },
  { value: 'signals', label: 'Signals', icon: Activity },
  { value: 'persona', label: 'Personas', icon: User },
  { value: 'rendering', label: 'Rendering', icon: Monitor },
  { value: 'metrics', label: 'Metrics', icon: BarChart3 },
  { value: 'review', label: 'Human Review', icon: Eye },
  { value: 'export', label: 'Export', icon: Download },
  { value: 'comparison', label: 'Fidelity', icon: GitCompareArrows },
  // External links to existing pages
  { value: 'profiles', label: 'Profiles', icon: Brain, external: '/employee/mindmetal/profiles' },
  { value: 'attestation', label: 'Attestation', icon: Shield, external: '/employee/attestations' },
  { value: 'calibration', label: 'Calibration', icon: Sliders, external: '/settings/calibration/overview' },
  { value: 'governance', label: 'Governance', icon: Scale, external: '/settings/governance/retention' },
]

export default function MindMetaLAdmin() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'test-users')

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
  }, [tabFromUrl])

  const handleTabChange = (value: string) => {
    // Check if this is an external link tab
    const tab = TABS.find((t) => t.value === value)
    if (tab?.external) {
      return // Don't switch — the Link handles navigation
    }
    setActiveTab(value)
    setSearchParams({ tab: value })
  }

  const tabTriggerClasses =
    'flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-3 py-2 text-sm whitespace-nowrap'

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-copper-400 to-copper-600 flex items-center justify-center">
          <Brain className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-midnight-900">Personic AI Admin</h1>
          <p className="text-warm-500">Personality profiling system configuration and testing</p>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="bg-warm-100 p-1 rounded-xl inline-flex min-w-full">
            {TABS.map((tab) => {
              const Icon = tab.icon
              if (tab.external) {
                return (
                  <Link
                    key={tab.value}
                    to={tab.external}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm whitespace-nowrap text-warm-500 hover:text-midnight-700 hover:bg-warm-50 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </Link>
                )
              }
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={tabTriggerClasses}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </TabsTrigger>
              )
            })}
          </TabsList>
        </div>

        <TabsContent value="test-users" className="mt-6">
          <TestUsersTab />
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <ConfigTab />
        </TabsContent>

        <TabsContent value="distillation" className="mt-6">
          <DistillationTesterTab />
        </TabsContent>

        <TabsContent value="signals" className="mt-6">
          <SignalsTab />
        </TabsContent>

        <TabsContent value="persona" className="mt-6">
          <PersonaTesterTab />
        </TabsContent>

        <TabsContent value="rendering" className="mt-6">
          <RenderingTab />
        </TabsContent>

        <TabsContent value="metrics" className="mt-6">
          <MetricsTab />
        </TabsContent>

        <TabsContent value="review" className="mt-6">
          <HumanReviewTab />
        </TabsContent>

        <TabsContent value="export" className="mt-6">
          <ExportTab />
        </TabsContent>

        <TabsContent value="comparison" className="mt-6">
          <ComparisonTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
